import { useCallback, useMemo, useState } from 'react';
import { useOperationsHistory } from '@/shared/api';
import type { OperationHistoryParams } from '@/shared/types';
import {
  useOperationsHistoryFrames,
  useResetOperationsHistoryFrames,
} from '../api';
import { combineOperationFrames, countTrailingEmptyFrames } from '../lib';

const MAX_AUTO_EMPTY_FRAMES = 5;

type FramesState = {
  frameId?: string;
  paramsKey?: string;
  resumeIndex: number;
};

export const useHistoryOperations = (queryParams: OperationHistoryParams) => {
  const isPeriodApplied = Object.values(queryParams).some(Boolean);
  const paramsKey = JSON.stringify(queryParams);
  const resetFrames = useResetOperationsHistoryFrames();

  const {
    operationsHistory,
    isOperationsHistoryLoading,
    operationsHistoryError,
  } = useOperationsHistory('all', queryParams);

  const firstFrame = useMemo(
    () => operationsHistory?.operations ?? [],
    [operationsHistory]
  );
  const isFirstFrameEmpty = !firstFrame.length;
  const firstNextFrameId =
    isPeriodApplied || isFirstFrameEmpty
      ? undefined
      : operationsHistory?.nextFrameId || undefined;

  const [framesState, setFramesState] = useState<FramesState>({
    resumeIndex: 0,
  });
  const isFramesRequested =
    !!firstNextFrameId &&
    framesState.frameId === firstNextFrameId &&
    framesState.paramsKey === paramsKey;

  const {
    data: framesData,
    hasNextPage: hasNextFrame,
    fetchNextPage,
    refetch: refetchFrames,
    isFetching: isFramesFetching,
    isError: isFramesError,
  } = useOperationsHistoryFrames({
    firstNextFrameId,
    enabled: isFramesRequested,
  });

  const olderFrames = useMemo(
    () => framesData?.pages.map(page => page.operations ?? []) ?? [],
    [framesData]
  );

  const operations = useMemo(
    () => combineOperationFrames(firstFrame, olderFrames),
    [firstFrame, olderFrames]
  );

  const hasMore = isFramesRequested ? hasNextFrame : !!firstNextFrameId;
  const resumeIndex = isFramesRequested ? framesState.resumeIndex : 0;
  const isLoadMoreRequired =
    hasMore &&
    countTrailingEmptyFrames(olderFrames, resumeIndex) >= MAX_AUTO_EMPTY_FRAMES;

  const loadNextFrame = useCallback(() => {
    if (isFramesFetching) return;

    if (!isFramesRequested) {
      resetFrames();
      setFramesState({ frameId: firstNextFrameId, paramsKey, resumeIndex: 0 });
      return;
    }

    void fetchNextPage();
  }, [
    isFramesFetching,
    isFramesRequested,
    firstNextFrameId,
    paramsKey,
    resetFrames,
    fetchNextPage,
  ]);

  const loadMore = useCallback(() => {
    setFramesState(state => ({ ...state, resumeIndex: olderFrames.length }));
    void fetchNextPage();
  }, [olderFrames.length, fetchNextPage]);

  const retry = useCallback(() => {
    if (framesData?.pages.length) {
      void fetchNextPage();
      return;
    }

    void refetchFrames();
  }, [framesData, fetchNextPage, refetchFrames]);

  return {
    operations,
    isFirstFrameLoading: isOperationsHistoryLoading,
    firstFrameError: operationsHistoryError,
    isFirstFrameEmpty,
    hasMore,
    isFetchingMore: isFramesRequested && isFramesFetching,
    isLoadMoreError: isFramesRequested && isFramesError && !isFramesFetching,
    isLoadMoreRequired,
    loadNextFrame,
    loadMore,
    retry,
  };
};
